import { themeQuartz, iconSetQuartzLight } from 'ag-grid-community';

// The height of every grid row, in px. Exported for a caller that sizes a
// container to its rows (PurchaseOrderForm's proposed-lines grid).
export const ROW_HEIGHT = 44;

/**
 * Stable AG Grid theme that uses CSS custom properties (var()) for all color params.
 *
 * Created once at module load — never recreated. Colors update automatically when
 * data-theme changes because var() references are resolved by the browser at paint time.
 * This prevents AG Grid from reinitializing on theme toggle, eliminating the blink.
 */
const agGridTheme = themeQuartz
  .withPart(iconSetQuartzLight)
  .withParams({
    accentColor: 'var(--color-accent)',
    // An autoHeight grid is as tall as its rows. The floor is one row, not 0:
    // the loading and no-rows overlays draw inside the body and need its room.
    autoHeightMinBodyHeight: ROW_HEIGHT,
    backgroundColor: 'var(--color-surface)',
    borderColor: 'var(--color-border)',
    borderRadius: 2,
    cellHorizontalPaddingScale: 0.7,
    chromeBackgroundColor: { ref: 'backgroundColor' },
    columnBorder: false,
    fontFamily: 'inherit',
    // Type comes from the token, not a literal, because the grid is not the only
    // surface that renders at this size: action menus (styles/mui-overrides.css,
    // `.app-action-menu`) float over the grid and have to match it. One home for
    // the pair, asserted in components/common/ActionMenu.test.jsx.
    fontSize: 'var(--font-size-grid)',
    foregroundColor: 'var(--color-text-secondary)',
    headerBackgroundColor: 'var(--color-surface)',
    headerFontSize: 'var(--font-size-grid)',
    // What tells a column header row from the rows under it, in every grid
    // the app draws: the brand colour and a line beneath (styles/pages.css,
    // MASTER-DETAIL ROW STYLING, gives a detail grid its row lines the same way).
    // The face stays regular: the app's semibold is a separate, rounder font
    // (Segoe UI Semibold) that reads too heavy over a grid of regular rows.
    headerFontWeight: 'var(--font-weight-normal)',
    headerRowBorder: true,
    headerTextColor: 'var(--color-primary)',
    headerVerticalPaddingScale: 1,
    oddRowBackgroundColor: 'var(--color-fill)',
    // A pinned column (the Forecast grid's product columns) reads as part of
    // the row, not as a separate panel.
    pinnedColumnBorder: false,
    rowBorder: false,
    rowVerticalPaddingScale: 0.8,
    rowHoverColor: 'transparent',
    sidePanelBorder: true,
    spacing: 6,
    wrapperBorder: false,
    wrapperBorderRadius: 0,
    checkboxUncheckedBorderColor: 'var(--color-border-hover)',
    checkboxCheckedBackgroundColor: 'var(--color-primary)',
    checkboxCheckedBorderColor: 'var(--color-primary)',
    checkboxCheckedShapeColor: 'var(--color-on-primary)',
    checkboxIndeterminateBackgroundColor: 'var(--color-primary)',
    checkboxIndeterminateBorderColor: 'var(--color-primary)',
    checkboxIndeterminateShapeColor: 'var(--color-on-primary)',
    // Pinned totals row — heavier top border, fill background to read
    // as "summary, not data". Bold typography handled via .ag-row-pinned-total CSS.
    pinnedRowBorder: { color: 'var(--color-border)', width: 2 },
    pinnedRowBackgroundColor: 'var(--color-fill)',
    // The app's one tooltip card (styles/variables.css, TOOLTIPS). Its corners,
    // shadow and padding have no param: styles/grid-page.css sets them.
    tooltipBackgroundColor: 'var(--tooltip-background)',
    tooltipTextColor: 'var(--tooltip-text)',
    tooltipBorder: 'var(--tooltip-border)',
    // One row height for every grid, tall enough for the Product cell's
    // thumbnail + name + SKU lines (components/grid/ProductCell.jsx), so a grid
    // with that column and a grid without it look the same.
    //
    // It must be a theme PARAMETER, not the `rowHeight` grid option. The
    // Theming API derives each cell's line-height — which is what vertically
    // centres plain-text cells — from the `--ag-row-height` variable
    // (ag-grid.css). The grid option sets the row's DOM height WITHOUT updating
    // that variable, so the text would top-align. No grid sets the `rowHeight`
    // grid option (it is absent from defaultGridProps too).
    rowHeight: ROW_HEIGHT,
  });

// Every grid uses the theme as it is; nothing passes a per-grid variant, because
// passing a freshly-created theme re-initialises the grid (the blink this module
// exists to avoid).
export function useAgGridTheme() {
  return agGridTheme;
}
